import React, { useState } from 'react';
import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';

import Modal from '../Modal';
import Icon from '../Icon';
import IconButton from '../IconButton';

const isProduction = process.env.NODE_ENV === 'production';

const DefaultFallback = ({ error, componentStack, context, resetErrorBoundary, fallbackRoute }) => {
    const { t } = useTranslation('ErrorBoundary');
    const [showDetails, setShowDetails] = useState(false);
    const title = `${t('Something went wrong')}${!isProduction && context ? ` ${t('in')} ${context}` : ''}.`;
    const subtitle = t('Sorry, something went wrong there. Try again.');
    return (
        <div className="flex h-full w-full items-center justify-center">
            <div className="w-full rounded-lg bg-[#0a1929] p-8 shadow-xl border border-gray-800 animate-fadeIn">
                {/* Header with Icon */}
                <div className="mb-6 flex items-start gap-4">
                    <div className="flex-shrink-0">
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-500/20 border border-red-500/30">
                            <Icon
                                name="icon-alert-outline"
                                className="text-red-500"
                                style={{ width: '24px', height: '24px' }}
                            />
                        </div>
                    </div>
                    <div className="flex-1">
                        <h2 className="text-xl font-semibold text-white leading-tight">
                            {title}
                        </h2>
                        <p className="mt-2 text-sm text-gray-400">
                            {subtitle}
                        </p>
                    </div>
                </div>

                {/* Action Button */}
                <div className="mb-6">
                    <button
                        onClick={() => {
                            if (typeof window !== 'undefined') {
                                if (fallbackRoute) {
                                    window.location.href = fallbackRoute;
                                } else {
                                    // Reload the current page
                                    window.location.reload();
                                }
                            } else {
                                // Fallback for SSR environments
                                resetErrorBoundary();
                            }
                        }}
                        className="rounded bg-[#1976d2] px-6 py-2.5 font-medium text-white transition-all duration-200 hover:bg-[#1565c0] hover:shadow-lg hover:shadow-blue-500/30 focus:outline-none focus:ring-2 focus:ring-[#1976d2] focus:ring-offset-2 focus:ring-offset-[#0a1929] active:scale-95"
                    >
                        {t('Reload Page')}
                    </button>
                </div>

                {/* Error Details - Only in Development */}
                {!isProduction && error && (
                    <div className="space-y-4">
                        {/* Context */}
                        {context && (
                            <div className="pb-4 border-b border-gray-800/50">
                                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
                                    {t('Context')}:
                                </p>
                                <p className="font-mono text-sm text-gray-300 bg-black/40 px-3 py-2 rounded">
                                    {context}
                                </p>
                            </div>
                        )}

                        {/* Error Message */}
                        <div className="pb-4 border-b border-gray-800/50">
                            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
                                {t('Error Message')}:
                            </p>
                            <p className="font-mono text-sm text-red-400 bg-red-500/10 px-3 py-2 rounded border border-red-500/20">
                                {error.message}
                            </p>
                        </div>

                        {/* Stack Trace Toggle */}
                        <div>
                            <button
                                onClick={() => setShowDetails(!showDetails)}
                                className="flex items-center gap-2 rounded bg-black/30 px-4 py-3 w-full font-medium text-gray-300 hover:bg-black/40 transition-colors focus:outline-none"
                            >
                                <Icon
                                    name="chevron-down"
                                    className={`transition-transform duration-200 ${showDetails ? 'rotate-180' : ''}`}
                                    style={{ width: '16px', height: '16px' }}
                                />
                                {t('Stack Trace')}
                            </button>

                            {showDetails && (
                                <div className="mt-4 rounded bg-black/30 p-5 border border-gray-800/50">
                                    <pre
                                        className="max-h-64 overflow-auto rounded bg-black/50 p-4 text-xs text-gray-400 border border-gray-800/50 leading-relaxed whitespace-pre-wrap"
                                        style={{
                                            scrollbarWidth: 'thin',
                                            scrollbarColor: '#4B5563 transparent',
                                        }}
                                    >
                                        {error.stack}
                                    </pre>
                                    {componentStack && (
                                        <>
                                            <p className="mt-4 mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
                                                {t('Component Stack')}:
                                            </p>
                                            <pre
                                                className="max-h-64 overflow-auto rounded bg-black/50 p-4 text-xs text-gray-400 border border-gray-800/50 leading-relaxed whitespace-pre-wrap"
                                                style={{
                                                    scrollbarWidth: 'thin',
                                                    scrollbarColor: '#4B5563 transparent',
                                                }}
                                            >
                                                {componentStack}
                                            </pre>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const noop = () => { };

DefaultFallback.propTypes = {
    error: PropTypes.object.isRequired,
    resetErrorBoundary: PropTypes.func,
    componentStack: PropTypes.string,
    context: PropTypes.string,
    fallbackRoute: PropTypes.string,
};

DefaultFallback.defaultProps = {
    resetErrorBoundary: noop,
    componentStack: null,
    context: null,
    fallbackRoute: null,
};

const ErrorBoundary = ({
    context,
    onReset,
    onError,
    fallbackComponent: FallbackComponent,
    children,
    fallbackRoute,
    isPage,
    resetKeys,
}) => {
    const [isOpen, setIsOpen] = useState(true);
    const [componentStack, setComponentStack] = useState(null);

    const onErrorHandler = (error, info) => {
        console.error(`${context} Error Boundary`, error, info.componentStack, context);
        setComponentStack(info.componentStack);
        onError(error, info.componentStack, context);
    };

    const onResetHandler = (...args) => {
        setComponentStack(null);
        onReset(...args);
    };

    const withModal = Component => props => (
        <Modal
            closeButton
            shouldCloseOnEsc
            isOpen={isOpen}
            title={i18n.t('ErrorBoundary:Something went wrong')}
            onClose={() => {
                setIsOpen(false);
                if (fallbackRoute && typeof window !== 'undefined') {
                    window.location.href = fallbackRoute;
                }
            }}
        >
            <Component {...props} />
        </Modal>
    );

    const Fallback = isPage ? FallbackComponent : withModal(FallbackComponent);

    return (
        <ReactErrorBoundary
            fallbackRender={(fallbackProps) => (
                <Fallback
                    {...fallbackProps}
                    context={context}
                    fallbackRoute={fallbackRoute}
                    componentStack={componentStack}
                />
            )}
            onReset={onResetHandler}
            onError={onErrorHandler}
            resetKeys={resetKeys}
        >
            {children}
        </ReactErrorBoundary>
    );
};

ErrorBoundary.propTypes = {
    context: PropTypes.string,
    onReset: PropTypes.func,
    onError: PropTypes.func,
    fallbackComponent: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
    children: PropTypes.node.isRequired,
    fallbackRoute: PropTypes.string,
    isPage: PropTypes.bool,
    resetKeys: PropTypes.array,
};

ErrorBoundary.defaultProps = {
    context: 'OHIF',
    onReset: noop,
    onError: noop,
    fallbackComponent: DefaultFallback,
    fallbackRoute: null,
    isPage: false,
    resetKeys: [],
};

export default ErrorBoundary;
